// O texto do local de retirada/devolução no card (#5125) — puro, sem React. O
// fornecedor não diz onde a agência fica (sem cidade, IATA nem tipo de
// balcão); o lugar BUSCADO diz. Quando a agência é o lugar buscado, o card
// repete o rótulo da busca sem o parentético ("Santos Dumont (SDU)" → "Santos
// Dumont", #6390: a pílula do topo já o diz); senão, o nome tratado pelo BFF
// (`cleanAgencyName`). A distância é sinal interno, nunca número na tela:
// a âncora de um aeroporto é um ponto arbitrário.
import { distanceKm } from '../../shared/distance_km.js';

/** O tipo do lugar buscado: aeroporto, endereço/hotel/POI ou cidade/região. */
type RentalPlaceKind = 'airport' | 'address' | 'city';

type Coordinates = { latitude: number; longitude: number };

/** Um ponto da busca como a página o resolveu. */
export interface RentalSearchedPlace {
  /** O rótulo da busca, o mesmo da pílula. */
  label: string;
  /** Ausente (a conversa não ecoa o lugar pedido) ⇒ o card nunca troca o nome. */
  kind?: RentalPlaceKind;
  iata?: string;
  latitude?: number;
  longitude?: number;
}

/** A agência de uma ponta da oferta. */
export interface RentalAgencyFacts {
  /** O nome cru do fornecedor — é nele que o IATA e o nome do aeroporto casam. */
  name?: string;
  /** O nome tratado pelo BFF (ou o endereço, quando o nome é genérico). */
  label?: string;
  address?: string;
  coordinates?: Coordinates;
}

/** Aeroporto casa pelo nome; o raio só veta o balcão longe ("Centro (Shuttle
 *  para o Aeroporto)" em Guarulhos), por isso é largo: terminais distam 3 km. */
const AIRPORT_RADIUS_KM = 3;
/** Endereço, hotel ou POI casa pelo raio. */
const ADDRESS_RADIUS_KM = 1;

/** Palavras que não distinguem um aeroporto nem uma agência de outra. */
const GENERIC_WORDS = new Set([
  'AEROPORTO',
  'AIRPORT',
  'AEROPUERTO',
  'INTERNACIONAL',
  'INTERNATIONAL',
  'INTL',
  'LOJA',
  'AGENCIA',
  'FILIAL',
]);

/** Maiúscula sem acento nem pontuação, palavras separadas por um espaço. */
function foldWords(text: string): string[] {
  return text
    .normalize('NFD')
    .replaceAll(/\p{M}/gu, '')
    .toUpperCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
}

function containsPhrase(haystack: readonly string[], needle: readonly string[]): boolean {
  if (needle.length === 0) return false;
  return ` ${haystack.join(' ')} `.includes(` ${needle.join(' ')} `);
}

/** O texto sem os trechos entre parênteses ("Santos Dumont (SDU)" → "Santos Dumont "), por
 *  índice: um `(` sem `)` adiante para a busca em vez de ser retentado a cada posição. */
function withoutParenthesized(text: string): string {
  let out = '';
  let from = 0;
  for (let open = text.indexOf('('); open !== -1; open = text.indexOf('(', from)) {
    const close = text.indexOf(')', open);
    if (close === -1) break;
    out += `${text.slice(from, open)} `;
    from = close + 1;
  }
  return out + text.slice(from);
}

/** O nome da agência fala do aeroporto buscado: traz o IATA, a palavra
 *  "Aeroporto"/"Airport" ou o nome do aeroporto (o rótulo sem o "(SDU)"). */
function namesAirport(name: string, place: RentalSearchedPlace): boolean {
  const words = foldWords(name);
  const iata = place.iata?.toUpperCase();
  if (iata && words.includes(iata)) return true;
  if (words.some((word) => word === 'AEROPORTO' || word === 'AIRPORT' || word === 'AEROPUERTO')) {
    return true;
  }
  const airportName = foldWords(withoutParenthesized(place.label)).filter(
    (word) => !GENERIC_WORDS.has(word) && word !== iata,
  );
  return airportName.join('').length >= 4 && containsPhrase(words, airportName);
}

/** A agência É o lugar buscado, pela regra do tipo do lugar. Sem coordenada da
 *  agência (o cache do upstream às vezes a omite) nada casa: o nome sozinho não
 *  prova o balcão no aeroporto ("Guarulhos - Centro (Shuttle para o Aeroporto)"
 *  casa por nome e fica a 7 km), então vale o nome tratado. Quando a
 *  rental-car-api disser se a agência é de aeroporto, esse fato substitui a
 *  heurística de nome. */
function agencyIsSearchedPlace(agency: RentalAgencyFacts, place: RentalSearchedPlace): boolean {
  const { latitude, longitude } = place;
  const distance =
    agency.coordinates && latitude !== undefined && longitude !== undefined
      ? distanceKm({ latitude, longitude }, agency.coordinates)
      : undefined;
  switch (place.kind) {
    case 'airport':
      return (
        distance !== undefined &&
        distance <= AIRPORT_RADIUS_KM &&
        namesAirport(agency.name ?? '', place)
      );
    case 'address':
      return distance !== undefined && distance <= ADDRESS_RADIUS_KM;
    case 'city':
    case undefined:
    default:
      // Cidade nunca: seria "Rio de Janeiro" em todos os cards.
      return false;
  }
}

/** Uma ponta de uma oferta da lista: a locadora (chave da colisão e nome do
 *  fallback) e a agência. */
export interface RentalStopEntry {
  companyKey: string;
  companyName: string;
  agency: RentalAgencyFacts;
}

type StopLabel = { value: string; searched: boolean; entry: RentalStopEntry };

function stopLabelOf(
  entry: RentalStopEntry,
  place: RentalSearchedPlace,
  fallback: (company: string) => string,
): StopLabel {
  if (place.label && agencyIsSearchedPlace(entry.agency, place)) {
    const short = withoutParenthesized(place.label).replaceAll(/\s+/g, ' ').trim();
    return { value: short || place.label, searched: true, entry };
  }
  const value = entry.agency.label?.trim() || entry.agency.name?.trim();
  return { value: value || fallback(entry.companyName), searched: false, entry };
}

/** O que distingue a agência das outras com o mesmo rótulo da busca: o nome
 *  tratado sem as palavras do rótulo nem as genéricas ("GRU Terminal 2" em
 *  "Guarulhos (GRU)" → "Terminal 2"). Nunca o endereço inteiro. */
function disambiguatorOf(label: StopLabel, place: RentalSearchedPlace): string {
  const name = label.entry.agency.label ?? label.entry.agency.name ?? '';
  const known = new Set([...foldWords(place.label), ...GENERIC_WORDS]);
  const kept = name.split(' ').filter((word) => {
    const folded = foldWords(word);
    return folded.length > 0 && folded.every((part) => !known.has(part));
  });
  return kept.join(' ');
}

function identityOf(agency: RentalAgencyFacts): string {
  return foldWords(`${agency.name ?? ''} ${agency.address ?? ''}`).join(' ');
}

/**
 * O texto de UMA ponta (retirada ou devolução) de cada oferta da lista, contra
 * o lugar buscado daquela ponta. Colisão — duas agências diferentes da mesma
 * locadora com o mesmo rótulo da busca (Localiza no Terminal 1 e no Terminal 2
 * de GRU) — troca o rótulo por um desambiguador curto vindo do nome ("Terminal
 * 2"): somado ao rótulo, reticenciava antes de chegar nele (#6390).
 */
export function rentalStopLabelsOf(
  entries: readonly RentalStopEntry[],
  place: RentalSearchedPlace,
  fallback: (company: string) => string,
): string[] {
  const labels = entries.map((entry) => stopLabelOf(entry, place, fallback));
  const groups = new Map<string, StopLabel[]>();
  for (const label of labels) {
    if (!label.searched) continue;
    const key = `${label.entry.companyKey}\u0000${label.value}`;
    groups.set(key, [...(groups.get(key) ?? []), label]);
  }
  const disambiguators = new Map<StopLabel, string>();
  for (const group of groups.values()) {
    if (new Set(group.map((label) => identityOf(label.entry.agency))).size < 2) continue;
    const parts = group.map((label) => disambiguatorOf(label, place));
    // Só desambigua o que de fato separa: partes iguais ou vazias não dizem nada.
    if (new Set(parts).size < 2) continue;
    group.forEach((label, index) => {
      const part = parts[index];
      if (part) disambiguators.set(label, part);
    });
  }
  return labels.map((label) => disambiguators.get(label) ?? label.value);
}
